/**
 * PasskeyPromptBanner — proaktiver "Aktiviere Face ID jetzt"-Hinweis.
 *
 * Sichtbar genau dann, wenn ALLE Bedingungen wahr sind:
 *   - User ist eingeloggt UND hat noch keinen Passkey (`!user.has_passkeys`)
 *   - Browser unterstützt WebAuthn (`isPasskeySupported()`)
 *   - User hat den Hinweis nicht in letzter Zeit ausgeblendet
 *     (localStorage `passkey_prompt_dismissed_until` Datum noch in der Zukunft)
 *
 * Aktionen:
 *   - "Jetzt aktivieren"     → enrollPasskey + Banner verschwindet (refreshUser)
 *   - "Später erinnern"      → in 7 Tagen wieder zeigen
 *   - "Nicht mehr fragen"    → in 1 Jahr wieder zeigen
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, X, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isPasskeySupported, enrollPasskey } from "@/lib/passkey";

const DISMISS_KEY = "passkey_prompt_dismissed_until";

export default function PasskeyPromptBanner() {
  const { user, refresh } = useAuth();
  const [enrolling, setEnrolling] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) { setVisible(false); return; }
    if (user.has_passkeys) { setVisible(false); return; }
    if (!isPasskeySupported()) { setVisible(false); return; }
    try {
      const until = localStorage.getItem(DISMISS_KEY);
      if (until && Date.now() < Number(until)) {
        setVisible(false);
        return;
      }
    } catch { /* localStorage blocked → still show */ }
    setVisible(true);
  }, [user]);

  if (!visible) return null;

  const remind = (days) => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
    setVisible(false);
  };

  const handleEnroll = async () => {
    if (enrolling) return;
    setEnrolling(true);
    try {
      const guess = navigator.userAgent.includes("iPhone") ? "iPhone"
        : navigator.userAgent.includes("Android") ? "Android-Handy"
        : navigator.userAgent.includes("Mac") ? "Mac"
        : navigator.userAgent.includes("Windows") ? "Windows-PC"
        : "Mein Gerät";
      await enrollPasskey(guess);
      toast.success("Face ID / Touch ID aktiviert!", {
        description: "Beim nächsten Login einfach auf 'Mit Face ID anmelden' tippen.",
      });
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      // Refresh user so `has_passkeys` updates and the banner disappears.
      if (typeof refresh === "function") {
        await refresh();
      }
      setVisible(false);
    } catch (e) {
      toast.error(e?.message || "Passkey-Anlage fehlgeschlagen", {
        description: "Sie können es später in 'Mein Profil' erneut versuchen.",
      });
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div
      data-testid="passkey-prompt-banner"
      className="relative overflow-hidden rounded-2xl border border-[#00E575]/30 bg-gradient-to-br from-[#00E575]/[0.08] via-[#0d2a1a]/40 to-[#09090B] p-5 mb-5 shadow-lg"
    >
      <button
        data-testid="passkey-prompt-dismiss"
        onClick={() => remind(7)}
        aria-label="Hinweis schließen"
        className="absolute top-3 right-3 p-1.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/60 transition"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#00E575]/15 flex items-center justify-center shrink-0 ring-1 ring-[#00E575]/30">
          <Fingerprint className="text-[#00E575]" size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={13} className="text-[#00E575]" />
            <span className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold">Neu — schneller einloggen</span>
          </div>
          <h3 className="font-display text-lg font-semibold text-zinc-100 leading-tight">
            Mit Face ID statt OTP-Code anmelden
          </h3>
          <p className="text-sm text-zinc-300 mt-1.5 leading-snug">
            Spart Ihnen 10 Sekunden pro Login. Funktioniert mit Face ID, Touch ID, Fingerabdruck oder Windows Hello — ein Tipp genügt.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <Button
          data-testid="passkey-prompt-enroll"
          onClick={handleEnroll}
          disabled={enrolling}
          className="h-11 flex-1 bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964] active:scale-[0.97] transition disabled:opacity-60"
        >
          {enrolling ? <Loader2 size={16} className="animate-spin" /> : <><Fingerprint size={16} className="mr-2" /> Jetzt aktivieren</>}
        </Button>
        <Button
          data-testid="passkey-prompt-later"
          onClick={() => remind(7)}
          variant="outline"
          className="h-11 sm:flex-none border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          Später erinnern
        </Button>
        <Button
          data-testid="passkey-prompt-never"
          onClick={() => remind(365)}
          variant="ghost"
          className="h-11 sm:flex-none text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900"
        >
          Nicht mehr fragen
        </Button>
      </div>
    </div>
  );
}
